import React, { useCallback } from "react"
import { createStackNavigator } from "@react-navigation/stack"
import {
  AutoFeederPondActivityListScreen, AutoFeederPondActivityListTitle,
  AutoFeederPondCreateScreen,
  AutoFeederPondDetailScreen, AutoFeederPondDetailActivity,
  AutoFeederPondEditScreen,
  CycleDetailTitle,
  FarmAutoFeederPondListScreen, AutoFeederHeader,
  FeedCreateScreen,
  FeedingTrayCreateMultipleScreen,
  AutoFeederCreateQRScanScreen,
  WizardTestRunScreen,
  WizardConectionSetupScreen,
  WizardModeSetupScreen,
  AutoFeederPondDailyFeedCreateMultipleScreen
} from "../../../screens"
import { Text, useAutoFeederPond, JalaLoading } from "../../../components"
import { translate } from "../../../i18n"
import { color, nativeBaseTheme } from "../../../theme"
import { View } from "native-base"
import { CycleFeedRelatedNavigator } from "../cycle/cycle-navigator"
import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs"
import { Appearance, ViewStyle } from "react-native"
import { useStores } from "../../../models"
import { AutoFeederPond } from "../../../database"
import { useFocusEffect } from "@react-navigation/native"

export type HomeAutoFeederPondNavigatorParamList = {
  'Home.Farm.AutoFeederPond.List': undefined,
  'Home.AutoFeederPond.Create': undefined,
  'Home.AutoFeederPond.Detail.Stack': undefined,
  // 'Home.AutoFeederPond.Detail': undefined,
  'Home.AutoFeederPond.Edit': undefined,
  'Home.AutoFeederPond.Activity.List': undefined,

  'Home.Cycle.Stack': undefined,
  'Home.Feed.Create': undefined,
  'Home.Feed.Edit': undefined,
  'Home.FeedingTrayControl.CreateMultiple': undefined,
  'Home.FeedingTrayControl.EditMultiple': undefined,
  'Home.AutofeederPond.Create.QRScan': undefined,
  'Home.AutoFeederPond.Wizard.Connection.Setup': undefined,
  'Home.AutoFeederPond.Wizard.Mode.Setup': undefined,
  'Home.AutoFeederPond.Wizard.Test.Run': undefined,
  'Home.AutoFeederPond.DailyFeed.CreateMultiple': undefined
}

const TAB: ViewStyle = {
  // width: 'auto',
  // paddingHorizontal: 32,
  paddingVertical: 8
}

const Tab = createMaterialTopTabNavigator()

const TabScreen = (props) => {
  const { route: { params = {} } } = props
  const { appStore: { farmStore } } = useStores()
  const { setAutoFeederPond } = useAutoFeederPond()

  useFocusEffect(useCallback(() => {
    farmStore.pondStore.set('autoFeederPondId', params.id)
    setAutoFeederPond(AutoFeederPond.hydrate(farmStore.pondStore.autoFeederPond))
  }, [farmStore, params.id, setAutoFeederPond]))

  return farmStore.pondStore.autoFeederPondId === params.id
    ? <AutoFeederPondDetailScreen {...props} />
    : <View flex={1} alignItems="center" justifyContent="center"><JalaLoading /></View>
}

const AutoFeederPondDetailNavigator = ({ route }) => {
  const colorScheme = "light" || Appearance.getColorScheme()
  const { appStore: { farmStore: { pondStore: { autoFeederPonds } } } } = useStores();

  return (
    <Tab.Navigator
      screenOptions={{
        tabBarActiveTintColor: colorScheme === 'light' ? color.palette.gray700 : undefined,
        tabBarScrollEnabled: true,
        tabBarItemStyle: TAB,
        lazy: true,
        lazyPreloadDistance: 0
      }}
      initialRouteName={route.params?.id ? `Home.AutoFeederPond.DetailScreen.${route.params.id}` : undefined}
      backBehavior="none"
    >{autoFeederPonds.map((item, index) => (
      <Tab.Screen
        key={index}
        name={`Home.AutoFeederPond.DetailScreen.${item.id}`}
        component={TabScreen}
        initialParams={{ id: item.id }}
        options={{
          title: item.name,
          tabBarLabel({ color, focused }) {
            return <Text
              text={item.name}
              preset="title4"
              colorScheme={colorScheme}
              color={focused ? color : nativeBaseTheme.colors[colorScheme]["400"]}
            />
          }
        }}
      />
    ))}
    </Tab.Navigator>
  )
}

const Stack = createStackNavigator<HomeAutoFeederPondNavigatorParamList>()

export const AutoFeederPondNavigator = () => {
  const { appStore: { farmStore: { pondStore: { autoFeederPonds } } } } = useStores();

  return (
    <Stack.Navigator
      screenOptions={{
        cardStyle: { flex: 1 },
        headerShown: false,
        headerBackTitleVisible: false,
        headerTitleAlign: "left",
        headerTitleContainerStyle: { marginLeft: 0 }
      }}
      // initialRouteName="Home.Farm.AutoFeederPond.List"
    >
      <Stack.Screen
        name="Home.Farm.AutoFeederPond.List"
        component={FarmAutoFeederPondListScreen}
        options={{
          title: 'Farm / Auto Feeder List',
          headerTitle: translate('farmAutoFeederPondListScreen.title'),
          headerShown: true,
          headerShadowVisible: false,
          headerStyle: { backgroundColor: color.primary },
          headerTintColor: color.palette.white,
          header({ navigation, route }) {
            return <AutoFeederHeader navigation={navigation} route={route} />
          },
        }}
      />
      <Stack.Screen
        name="Home.AutoFeederPond.Create"
        component={AutoFeederPondCreateScreen}
        options={{
          title: 'Auto Feeder Create',
          headerTitle: translate('autoFeederPondCreateScreen.title'),
          headerShown: true,
          headerShadowVisible: false,
          headerStyle: { backgroundColor: color.primary },
          headerTintColor: color.palette.white
        }}
      />
      <Stack.Screen
        name="Home.AutoFeederPond.Detail.Stack"
        component={autoFeederPonds.length > 1 ? AutoFeederPondDetailNavigator : AutoFeederPondDetailScreen}
        options={{
          title: 'Auto Feeder Detail',
          // headerTitle: translate('autoFeederPondDetailScreen.title'),
          headerTitle({ tintColor }) {
            return <CycleDetailTitle tintColor={tintColor}/>
          },
          headerShown: true,
          headerShadowVisible: false,
          headerStyle: { backgroundColor: color.primary },
          headerTintColor: color.palette.white,
          headerRight() {
            return <View flexDirection="row">
              <AutoFeederPondDetailActivity />
              {/* <AutoFeederPondDetailSetting /> */}
            </View>
          }
        }}
      />
      <Stack.Screen
        name="Home.AutoFeederPond.Edit"
        component={AutoFeederPondEditScreen}
        options={{
          title: 'Auto Feeder Edit',
          headerTitle: translate('autoFeederPondEditScreen.title'),
          headerShown: true,
          headerShadowVisible: false,
          headerStyle: { backgroundColor: color.primary },
          headerTintColor: color.palette.white
        }}
      />
      <Stack.Screen
        name="Home.AutoFeederPond.Activity.List"
        component={AutoFeederPondActivityListScreen}
        options={{
          title: 'Auto Feeder / Activity',
          headerTitle({ tintColor }) {
            return <AutoFeederPondActivityListTitle tintColor={tintColor}/>
          },
          headerShown: true,
          headerShadowVisible: false,
          headerStyle: { backgroundColor: color.primary },
          headerTintColor: color.palette.white
        }}
      />
      <Stack.Screen
        name="Home.Cycle.Stack"
        // component={CycleDetailScreen}
        options={{
          title: 'Cycle Detail',
          headerTitle({ tintColor }) {
            return <CycleDetailTitle tintColor={tintColor}/>
          },
          headerShown: true,
          headerShadowVisible: true,
          headerStyle: { backgroundColor: color.primary },
          headerTintColor: color.palette.white
        }}
      >{CycleFeedRelatedNavigator}</Stack.Screen>
      <Stack.Screen
        name="Home.Feed.Create"
        component={FeedCreateScreen}
        options={{
          title: 'Feed Create',
          headerTitle: translate('feedCreateScreen.title'),
          headerShown: true,
          headerShadowVisible: true,
          headerStyle: { backgroundColor: color.primary },
          headerTintColor: color.palette.white
        }}
      />
      <Stack.Screen
        name="Home.Feed.Edit"
        component={FeedCreateScreen}
        options={{
          title: 'Feed Edit',
          headerTitle: translate('feedEditScreen.title'),
          headerShown: true,
          headerShadowVisible: true,
          headerStyle: { backgroundColor: color.primary },
          headerTintColor: color.palette.white
        }}
      />
      <Stack.Screen
        name="Home.FeedingTrayControl.CreateMultiple"
        component={FeedingTrayCreateMultipleScreen}
        options={{
          title: 'FeedingTrayControl Create',
          headerTitle: translate('feedingTrayControlCreateScreen.title'),
          headerShown: true,
          headerShadowVisible: true,
          headerStyle: { backgroundColor: color.primary },
          headerTintColor: color.palette.white
        }}
      />
      <Stack.Screen
        name="Home.FeedingTrayControl.EditMultiple"
        component={FeedingTrayCreateMultipleScreen}
        options={{
          title: 'FeedingTrayControl Edit',
          headerTitle: translate('feedingTrayControlEditScreen.title'),
          headerShown: true,
          headerShadowVisible: true,
          headerStyle: { backgroundColor: color.primary },
          headerTintColor: color.palette.white
        }}
      />
      <Stack.Screen
        name="Home.AutofeederPond.Create.QRScan"
        component={AutoFeederCreateQRScanScreen}
        options={{
          title: 'Auto Feeder Create QR Scan',
          headerTitle: translate('autofeederCreateQRScanScreen.title'),
          headerShown: true,
          // headerShadowVisible: true,
          headerStyle: { backgroundColor: color.palette.black },
          headerTintColor: color.palette.white
        }}
      />
      <Stack.Screen
        name="Home.AutoFeederPond.Wizard.Connection.Setup"
        component={WizardConectionSetupScreen}
        options={{
          title: 'Auto Feeder Wizard Bluetooth Setup',
          headerTitle: translate('autofeederCreateQRScanScreen.title'),
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="Home.AutoFeederPond.Wizard.Mode.Setup"
        component={WizardModeSetupScreen}
        options={{
          title: 'Auto Feeder Wizard Mode Setup',
          headerTitle: translate('autofeederCreateQRScanScreen.title'),
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="Home.AutoFeederPond.Wizard.Test.Run"
        component={WizardTestRunScreen}
        options={{
          title: 'Auto Feeder Wizard Test Run',
          headerTitle: translate('autofeederCreateQRScanScreen.title'),
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="Home.AutoFeederPond.DailyFeed.CreateMultiple"
        component={AutoFeederPondDailyFeedCreateMultipleScreen}
        options={{
          title: 'Auto Feeder Daily Feed Create Multiple',
          headerTitle: translate('autoFeederDailyFeedForm.title'),
          headerShown: true,
          headerShadowVisible: true,
          headerStyle: { backgroundColor: color.primary },
          headerTintColor: color.palette.white
        }}
      />
    </Stack.Navigator>
  )
}
